import React, { useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import RoomCard from "./RoomCard";
import { Users, Calendar } from "lucide-react";

interface RoomsListSectionProps {
  rooms: PropertyToEdit["rooms"];
  startDate?: string;
  endDate?: string;
  maxResidents?: number;
  onCheckAvailability?: () => void;
  propertyData?: PropertyToEdit;
  onRoomSelect?: (room: PropertyToEdit["rooms"][number]) => void;
}

const formatDate = (dateStr?: string) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  };
  return date.toLocaleDateString(undefined, options);
};

// New function for short date format (mobile)
const formatShortDate = (dateStr?: string) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
};

const RoomsListSection: React.FC<RoomsListSectionProps> = ({
  rooms,
  startDate,
  endDate,
  maxResidents = 5,
  onCheckAvailability,
  propertyData,
  onRoomSelect,
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);

  // 🐛 DEBUG: Check if propertyData is being passed to this component
  React.useEffect(() => {
    console.group("🏢 RoomsListSection Debug");
    console.log("📦 propertyData received:", propertyData);
    console.log("📦 propertyData exists:", !!propertyData);
    console.log("🔑 is_shared_property:", propertyData?.is_shared_property);
    console.log("🏠 property title:", propertyData?.title);
    console.log("🚪 rooms passed as prop:", rooms?.length);
    console.log("🚪 rooms in propertyData:", propertyData?.rooms?.length);
    console.log("📅 startDate:", startDate);
    console.log("📅 endDate:", endDate);
    console.groupEnd();
  }, [propertyData, rooms, startDate, endDate]);

  const handleRoomSelect = (room: PropertyToEdit["rooms"][number]) => {
    setSelectedRoomId(room.id);

    if (onRoomSelect) {
      onRoomSelect(room);
    }

    // On desktop, scroll to sidebar
    if (window.innerWidth >= 1024) {
      const sidebar = document.querySelector(".listingSectionSidebar__wrap");
      if (sidebar) {
        sidebar.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  if (!rooms || rooms.length === 0) {
    return (
      <div className="listingSection__wrap">
        <div className="p-6 bg-white dark:bg-neutral-800 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-700">
          <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
            Available Rooms
          </h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm">
            No rooms found for the selected property.
          </p>
        </div>
      </div>
    );
  }

  const formattedStart = formatDate(startDate);
  const formattedEnd = formatDate(endDate);
  const formattedStartShort = formatShortDate(startDate);
  const formattedEndShort = formatShortDate(endDate);
  const isCheckAvailability = !(formattedStart && formattedEnd);

  return (
    <section className="listingSection__wrap">
      {/* Header */}
      <div className="space-y-3 mb-6">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Room Options
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Select a room to view booking details and reserve
        </p>
      </div>

      <div className="w-full h-px bg-gradient-to-r from-transparent via-neutral-300 dark:via-neutral-700 to-transparent mb-6" />

      {/* Property Info Cards - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
        {/* Roommates Info */}
        <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10 rounded-xl border border-blue-200 dark:border-blue-800">
          <div className="p-2 bg-blue-100 dark:bg-blue-900/40 rounded-lg flex-shrink-0">
            <Users size={20} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-blue-600 dark:text-blue-400 font-medium">
              Shared Living
            </p>
            <p className="text-sm text-blue-900 dark:text-blue-100 font-semibold truncate">
              Up to {maxResidents}{" "}
              {maxResidents === 1 ? "resident" : "residents"}
            </p>
          </div>
        </div>

        {/* Date Selection */}
        <button
          onClick={isCheckAvailability ? onCheckAvailability : undefined}
          disabled={!isCheckAvailability}
          className={`flex items-center gap-3 p-4 rounded-xl border transition-all ${
            isCheckAvailability
              ? "bg-gradient-to-br from-gray-50 to-gray-100/50 dark:from-neutral-800 dark:to-neutral-900 border-gray-300 dark:border-neutral-700 hover:border-[#f5694b] dark:hover:border-[#f5694b] cursor-pointer"
              : "bg-gradient-to-br from-green-50 to-green-100/50 dark:from-green-900/20 dark:to-green-800/10 border-green-200 dark:border-green-800 cursor-default"
          }`}
        >
          <div
            className={`p-2 rounded-lg flex-shrink-0 ${
              isCheckAvailability
                ? "bg-gray-100 dark:bg-neutral-700"
                : "bg-green-100 dark:bg-green-900/40"
            }`}
          >
            <Calendar
              size={20}
              className={
                isCheckAvailability
                  ? "text-gray-600 dark:text-gray-400"
                  : "text-green-600 dark:text-green-400"
              }
            />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p
              className={`text-xs font-medium ${
                isCheckAvailability
                  ? "text-gray-600 dark:text-gray-400"
                  : "text-green-600 dark:text-green-400"
              }`}
            >
              {isCheckAvailability ? "Select Dates" : "Your Dates"}
            </p>
            {isCheckAvailability ? (
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                Check availability
              </p>
            ) : (
              <>
                {/* Mobile view - compact date display */}
                <p className="text-sm font-semibold text-green-900 dark:text-green-100 sm:hidden">
                  {formattedStartShort} → {formattedEndShort}
                </p>
                {/* Desktop view - full date display */}
                <p className="hidden sm:block text-sm font-semibold text-green-900 dark:text-green-100">
                  {formattedStart} → {formattedEnd}
                </p>
              </>
            )}
          </div>
        </button>
      </div>

      {/* Selected Room Banner - Mobile Friendly */}
      {selectedRoomId && (
        <div className="mb-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 border-2 border-green-300 dark:border-green-700 rounded-xl">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
              <svg
                className="w-4 h-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                Room Selected!
              </p>
              <p className="text-xs text-green-700 dark:text-green-300">
                <span className="hidden sm:inline">
                  Check the booking sidebar to complete your reservation
                </span>
                <span className="sm:hidden">
                  Tap the booking bar below to continue
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Rooms List - Mobile Optimized */}
      <div className="space-y-4">
        {rooms.map((room) => (
          <div key={room.id}>
            <RoomCard
              room={room}
              startDate={startDate}
              endDate={endDate}
              propertyData={propertyData}
              onSelectRoom={handleRoomSelect}
              isSelected={selectedRoomId === room.id}
            />
          </div>
        ))}
      </div>

      {/* Mobile Helper Text */}
      <div className="mt-6 p-4 bg-gray-50 dark:bg-neutral-800 rounded-xl border border-gray-200 dark:border-neutral-700 lg:hidden">
        <p className="text-xs text-center text-gray-600 dark:text-gray-400">
          💡 After selecting a room, a booking bar will appear at the bottom of
          your screen
        </p>
      </div>
    </section>
  );
};

export default RoomsListSection;
